// Display formatters for the metrics screen. All are pure; unit suffixes come from i18n via `t`.

import { format, parseISO } from "date-fns";
import { groupThousands } from "@/shared/lib/money";
import type { Money, Ratio } from "./types";

type T = (key: string) => string;

const DASH = "—";

/** Group an integer with spaces, keeping a leading minus. */
function groupInt(n: number): string {
  const r = Math.round(n);
  return (r < 0 ? "-" : "") + groupThousands(String(Math.abs(r)));
}

/** "1234567.50" -> "1 234 568 so'm". Invalid input -> "—". */
export function formatSom(value: Money | null, t: T): string {
  if (value === null) return DASH;
  const n = Number(value);
  if (!Number.isFinite(n)) return DASH;
  return `${groupInt(n)} ${t("metrics.unit.som")}`;
}

/** 0.8712 -> "87.1%". null -> "—". */
export function formatPct(ratio: Ratio): string {
  if (ratio === null || !Number.isFinite(ratio)) return DASH;
  return `${(ratio * 100).toFixed(1)}%`;
}

/** Integers are grouped ("12 345"); fractional values keep 1 dp ("34.5"). */
export function formatNum(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return DASH;
  if (Number.isInteger(n)) return groupInt(n);
  const [int, frac] = n.toFixed(1).split(".");
  return `${groupInt(Number(int))}.${frac}`;
}

/** "2026-06-09" -> "09.06.2026". null -> "—". */
export function formatDate(iso: string | null): string {
  if (!iso) return DASH;
  const d = parseISO(iso);
  return Number.isNaN(d.getTime()) ? iso : format(d, "dd.MM.yyyy");
}

/** Minutes -> "45 min" / "3.1 hour" / "2.4 day" (units from i18n). null -> "—". */
export function formatMinutes(m: number | null, t: T): string {
  if (m === null || !Number.isFinite(m)) return DASH;
  if (m <= 60) return `${Math.round(m)} ${t("metrics.unit.min")}`;
  if (m < 1440) return `${(m / 60).toFixed(1)} ${t("metrics.unit.hour")}`;
  return `${(m / 1440).toFixed(1)} ${t("metrics.unit.day")}`;
}

/** "4520.3" -> "$4 520" (whole dollars, space-grouped like so'm). null / invalid -> "—". */
export function formatUsd(value: Money | null): string {
  if (value === null) return DASH;
  const n = Number(value);
  if (!Number.isFinite(n)) return DASH;
  return `${n < 0 ? "-" : ""}$${groupInt(Math.abs(n))}`;
}
